import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { MessageSquare, Scale, Calculator, Clock, HelpCircle, Plus } from 'lucide-react';
import axiosInstance from '@/config/axios';
import { toast } from 'sonner';

interface Ticket {
  _id: string;
  type: 'legal' | 'accounting' | 'technical' | 'other' | string;
  subject: string;
  status: string;
  updatedAt: string | Date;
}

export function SupportTicketsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = async () => {
    try {
      const response = await axiosInstance.get('/api/support/tickets');
      setTickets(response.data);
    } catch (error) {
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-display font-black uppercase text-ink italic">Soporte Especializado</h1>
            <p className="text-serif italic text-ink/60 mt-2">Consultas legales, contables y técnicas para tu negocio.</p>
          </div>
          <Button className="h-14 px-8 gap-2">
            <Plus className="h-5 w-5" />
            NUEVA CONSULTA
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xs font-black uppercase tracking-[0.4em] text-terracotta mb-6">Tus Consultas</h2>
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <div key={i} className="h-24 brutalist-border bg-ink/5 animate-pulse" />
              ))
            ) : tickets.length > 0 ? (
              tickets.map((ticket) => (
                <div key={ticket._id} className="brutalist-border bg-parchment p-6 group hover:bg-ink hover:text-parchment transition-colors cursor-pointer">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-6">
                      <div className="w-12 h-12 bg-moss flex items-center justify-center text-parchment">
                        {ticket.type === 'legal' ? <Scale className="h-6 w-6" /> : <Calculator className="h-6 w-6" />}
                      </div>
                      <div>
                        <h3 className="text-lg font-display font-black uppercase italic mb-1">{ticket.subject}</h3>
                        <p className="text-[10px] font-black uppercase tracking-widest text-terracotta">{ticket.type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-black uppercase tracking-widest bg-ink text-parchment px-2 py-1 group-hover:bg-terracotta transition-colors">
                        {ticket.status}
                      </span>
                      <p className="text-[10px] mt-2 italic opacity-40">Actualizado: {new Date(ticket.updatedAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-20 text-center brutalist-border bg-ink/5 italic text-ink/40">
                No tienes consultas activas en este momento.
              </div>
            )}
          </div>

          <div className="space-y-8">
            <div className="brutalist-border bg-ink text-parchment p-8">
              <h3 className="text-xl font-display font-black uppercase italic mb-6">Preguntas Frecuentes</h3>
              <div className="space-y-6">
                <div className="space-y-2">
                  <h4 className="text-xs font-black uppercase tracking-widest text-terracotta">¿La comisión tiene IVA?</h4>
                  <p className="text-sm font-serif italic opacity-60">Depende de si eres persona natural o jurídica y el tipo de servicio...</p>
                </div>
                <div className="space-y-2">
                  <h4 className="text-xs font-black uppercase tracking-widest text-terracotta">¿Mandato exclusivo?</h4>
                  <p className="text-sm font-serif italic opacity-60">Recomendamos siempre firmar mandatos exclusivos para asegurar...</p>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-8 border-parchment text-parchment hover:bg-parchment hover:text-ink">VER TODO EL FAQ</Button>
            </div>

            <div className="brutalist-border bg-terracotta text-parchment p-8">
              <HelpCircle className="h-8 w-8 mb-4" />
              <h3 className="text-xl font-display font-black uppercase italic mb-2">¿Necesitas ayuda urgente?</h3>
              <p className="text-sm font-serif italic opacity-80 mb-6">Nuestro equipo legal y contable está listo para apoyarte.</p>
              <p className="text-lg font-display font-black">LLÁMANOS AL +56 2 2345 6789</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
